import { X } from 'lucide-react';
import { TooltipProvider } from '../../admin/components/shared/Tooltip.tsx';
import {
  documentFileName,
  documentRowKey,
  documentSizeBytes,
  formatFileSize
} from '../../utils/documentUtils';
import { PROCESS_LIBRARY_ROW_HEIGHT_PX } from './constants';
import { FlowchartDocumentCheckbox } from './FlowchartDocumentCheckbox.tsx';

export const PROCESS_FILES_READONLY_TOOLTIP =
  'Click Edit to upload files, select documents, or generate a flowchart.';

function AiDocumentLibraryRow({
  document,
  onToggleDocumentSelection,
  onRemoveDocument,
  showRemove = true,
  showFileSize = true,
  showDocumentSelection = true,
  interactionDisabled = false,
  interactionDisabledTooltip = PROCESS_FILES_READONLY_TOOLTIP,
  selectionLimit = null,
  selectedCount = 0,
  selectionLimitTooltip = null,
  /** When false, rows are selectable before `file_id` exists (AI cycle library). Default true for process flowchart. */
  selectionRequiresLinkedFile = true
}) {
  const rowKey = documentRowKey(document);
  const fileId = String(document?.file_id ?? '').trim();
  const displayName = documentFileName(document, document?.file_id);
  const sizeBytes = documentSizeBytes(document);
  const showMetaLine = showFileSize || document.isUploading || document.isPendingUpload;
  const awaitingLinkedFile = selectionRequiresLinkedFile && !fileId;

  const rowGridClass = showDocumentSelection
    ? showRemove
      ? 'grid gap-3 sm:grid-cols-[1.5rem_minmax(0,1fr)_2rem] sm:items-start'
      : 'grid gap-3 sm:grid-cols-[1.5rem_minmax(0,1fr)] sm:items-start'
    : showRemove
      ? 'grid gap-3 sm:grid-cols-[minmax(0,1fr)_2rem] sm:items-start'
      : 'grid gap-3 sm:grid-cols-[minmax(0,1fr)] sm:items-start';

  const atSelectionLimit =
    typeof selectionLimit === 'number' &&
    selectionLimit > 0 &&
    selectedCount >= selectionLimit &&
    !document.selected;
  const checkboxDisabled =
    interactionDisabled ||
    awaitingLinkedFile ||
    document.isUploading ||
    document.isPendingUpload ||
    atSelectionLimit;
  const checkboxTooltip = interactionDisabled
    ? interactionDisabledTooltip
    : atSelectionLimit
      ? selectionLimitTooltip
      : awaitingLinkedFile || document.isUploading || document.isPendingUpload
          ? 'Available after upload completes'
          : null;
  const isChecked = selectionRequiresLinkedFile
    ? Boolean(fileId && document.selected)
    : Boolean(document.selected);

  const rowHighlight = showDocumentSelection && document.selected;

  return (
    <div className={`px-4 py-3.5 ${rowHighlight ? 'bg-blue-50/50' : 'bg-white'}`}>
      <div className={rowGridClass}>
        {showDocumentSelection ? (
          <FlowchartDocumentCheckbox
            checked={isChecked}
            disabled={checkboxDisabled}
            onChange={(checked) => onToggleDocumentSelection(rowKey, checked)}
            ariaLabel={`Select ${displayName} for flowchart`}
            tooltip={checkboxTooltip}
          />
        ) : null}
        <div className="min-w-0">
          <p className="break-words text-sm font-medium leading-snug text-gray-900">{displayName}</p>
          {showMetaLine ? (
            <p className="mt-0.5 text-xs text-gray-500">
              {[
                showFileSize ? formatFileSize(sizeBytes) : null,
                document.isUploading || document.isPendingUpload
                  ? 'Uploading…'
                  : showFileSize && document.file_id
                    ? 'Ready'
                    : null
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          ) : null}
        </div>
        {/* Tags hidden until product enables file tagging on process documents.
        {showTags ? (
          <div className="min-w-0 sm:min-w-[26rem]">
            <div className="flex min-w-0 flex-nowrap items-center justify-start gap-1.5 sm:justify-end">
              {tags.length > 0 ? (
                tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex shrink-0 items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 py-1 pl-2.5 pr-1 text-[11px] font-medium leading-none text-indigo-700"
                    title={tag}
                  >
                    <span className="whitespace-nowrap">{getAiDocumentTagLabel(tag)}</span>
                    <button
                      type="button"
                      onClick={() => onRemoveDocumentTag(rowKey, tag)}
                      className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full leading-none text-indigo-500 transition-colors hover:bg-indigo-100 hover:text-indigo-800"
                      aria-label={`Remove ${tag} tag from ${displayName}`}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </span>
                ))
              ) : !tagCreatorOpen ? (
                <span className="inline-flex shrink-0 items-center rounded-full border border-gray-200 bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-500">
                  No tag
                </span>
              ) : null}
              {tagCreatorOpen ? (
                <div className="min-w-[10rem] flex-1">
                  <ReactSelect
                    inputId={`ai-doc-tags-${rowKey}`}
                    autoFocus
                    isClearable={false}
                    menuIsOpen
                    options={AI_DOCUMENT_TAG_OPTIONS.filter(
                      (option) => !tags.some((tag) => tag.toLowerCase() === option.value.toLowerCase())
                    )}
                    value={null}
                    onBlur={onCloseTagCreator}
                    onChange={(option) => {
                      if (option?.value && onAddDocumentTag(rowKey, option.value)) {
                        onCloseTagCreator();
                      }
                    }}
                    placeholder="Select tag"
                    className="text-xs"
                    classNamePrefix="ai-file-tag"
                    menuPortalTarget={aiTagMenuPortalTarget}
                    menuPosition="fixed"
                    styles={aiTagSelectStyles}
                    noOptionsMessage={() => 'No tags available'}
                  />
                </div>
              ) : tags.length < AI_FILE_TAG_LIMIT ? (
                <button
                  type="button"
                  onClick={() => onOpenTagCreator(rowKey)}
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-500 shadow-sm transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600"
                  aria-label={`Add tag to ${displayName}`}
                  title="Add tag"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
        */}
        {showRemove ? (
          <button
            type="button"
            onClick={() => onRemoveDocument(rowKey)}
            disabled={interactionDisabled}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={`Remove ${displayName}`}
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function AiDocumentLibrary({
  documents,
  activeTagDocumentId,
  onToggleDocumentSelection,
  onRemoveDocument,
  onRemoveDocumentTag,
  onOpenTagCreator,
  onCloseTagCreator,
  onAddDocumentTag,
  showRemove = true,
  showTags = false,
  showFileSize = true,
  showDocumentSelection = true,
  interactionDisabled = false,
  interactionDisabledTooltip = PROCESS_FILES_READONLY_TOOLTIP,
  selectionLimit = null,
  selectedCount = 0,
  selectionLimitTooltip = null,
  listTitle = 'Cycle document library',
  listMeta,
  emptyMessage = 'No Documents Found',
  hideOuterShell = false,
  /** When set, list scrolls after this many rows (process edit / details). */
  maxVisibleRows = null,
  selectionRequiresLinkedFile = true
}) {
  const shouldScrollList =
    typeof maxVisibleRows === 'number' &&
    maxVisibleRows > 0 &&
    documents.length > maxVisibleRows;
  const listMaxHeightPx =
    typeof maxVisibleRows === 'number' && maxVisibleRows > 0
      ? maxVisibleRows * PROCESS_LIBRARY_ROW_HEIGHT_PX
      : undefined;

  const listBody =
    documents.length === 0 ? (
      <div className="px-4 py-5 text-center text-sm text-gray-500">{emptyMessage}</div>
    ) : (
      <div
        className={`divide-y divide-gray-100${shouldScrollList ? ' overflow-y-auto overscroll-y-contain' : ''}`}
        style={shouldScrollList ? { maxHeight: listMaxHeightPx } : undefined}
      >
        {documents.map((document) => (
          <AiDocumentLibraryRow
            key={documentRowKey(document)}
            document={document}
            activeTagDocumentId={activeTagDocumentId}
            onToggleDocumentSelection={onToggleDocumentSelection}
            onRemoveDocument={onRemoveDocument}
            onRemoveDocumentTag={onRemoveDocumentTag}
            onOpenTagCreator={onOpenTagCreator}
            onCloseTagCreator={onCloseTagCreator}
            onAddDocumentTag={onAddDocumentTag}
            showRemove={showRemove}
            showTags={showTags}
            showFileSize={showFileSize}
            showDocumentSelection={showDocumentSelection}
            interactionDisabled={interactionDisabled}
            interactionDisabledTooltip={interactionDisabledTooltip}
            selectionLimit={selectionLimit}
            selectedCount={selectedCount}
            selectionLimitTooltip={selectionLimitTooltip}
            selectionRequiresLinkedFile={selectionRequiresLinkedFile}
          />
        ))}
      </div>
    );

  const header = (
    <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2.5">
      <span className="text-sm font-medium text-gray-700">{listTitle}</span>
      {listMeta ? <span className="text-xs font-medium text-gray-500">{listMeta}</span> : null}
    </div>
  );

  const shell = (
    <>
      {listTitle || listMeta ? header : null}
      {listBody}
    </>
  );

  const content = hideOuterShell ? (
    <div className="overflow-visible">{shell}</div>
  ) : (
    <div className="mb-4 overflow-visible rounded-xl border border-gray-200 bg-white">{shell}</div>
  );

  return <TooltipProvider>{content}</TooltipProvider>;
}
